import React, { useCallback, useEffect, useState } from 'react';
import 'moment/locale/de'; // Needed for date picker
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { ProfileService } from './api/ProfileService';
import { catchCallback } from './api/request';
import { AuthDialog, AUTH_MODE } from './auth/components/AuthDialog/AuthDialog';
import { ResetPage } from './auth/components/ResetPage';
import { SignupRedirect } from './auth/components/SignupRedirect';
import { CurrentUser } from './auth/services/CurrentUser';
import { onSessionExpired } from './auth/services/sessionEvents';
import { BookingPage } from './booking/components/BookingPage/BookingPage';
import AgbConsentModal from './common/components/AgbConsentModal/AgbConsentModal';
import { Footer } from './common/components/Footer';
import { Header } from './common/components/Header/Header';
import { getErrorMessageFromResponseError } from './common/utils/getErrorMessageFromResponseError';
import { InfoPage } from './info/InfoPage';
import LoyaltyPage from './loyalty/components/LoyaltyPage/LoyaltyPage';
import { LoyaltyProvider, useLoyalty } from './loyalty/LoyaltyContext';
import { ProfilePage } from './profile/components/ProfilePage/ProfilePage';
import { MuiProviders } from './common/components/MuiProviders';
import { useMetaTags } from './common/hooks/useMetaTags';
import { EmailView } from './email/EmailView';
import { UnsubscribePage } from './email/UnsubscribePage';
import { ConfirmEmailPage } from './email/ConfirmEmailPage';
import {
    setupAmplitudeInitially,
    initAmplitudeWithConsentCheck,
    syncConsentFromUrl,
} from './common/services/amplitudeService';
import CookieBanner from './common/components/CookieBanner';
import i18n from './localization/i18n';
import { DictionaryProvider } from './common/contexts/DictionaryContext';

// Redirect legacy HashRouter URLs (e.g. /#/booking) to BrowserRouter equivalents
(function redirectHashRoutes() {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#/')) {
        const hashContent = hash.slice(1); // remove '#', keep '/booking/...[?query]'
        const queryIndex = hashContent.indexOf('?');
        const pathname = queryIndex === -1 ? hashContent : hashContent.slice(0, queryIndex);

        // Merge the pre-hash query (e.g. ?lang=en) with any query carried inside the
        // hash so neither is lost; concatenating them would produce a broken `?a=1?b=2`.
        const mergedParams = new URLSearchParams(window.location.search);
        if (queryIndex !== -1) {
            new URLSearchParams(hashContent.slice(queryIndex + 1)).forEach((value, key) => {
                mergedParams.set(key, value);
            });
        }

        const queryString = mergedParams.toString();
        const newUrl = window.location.origin + pathname + (queryString ? `?${queryString}` : '');
        window.location.replace(newUrl);
    }
})();

// Call these once when the module is loaded, before the App component is defined.
setupAmplitudeInitially();
syncConsentFromUrl();

const setLanguageFromUrl = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const lang = searchParams.get('lang');
    if (lang) {
        i18n.changeLanguage(lang);
    }
};
setLanguageFromUrl();

const App = () => {
    const [authDialog, setAuthDialog] = useState({ open: false, mode: AUTH_MODE.LOGIN, email: '' });
    const openAuthDialog = useCallback(
        (mode = AUTH_MODE.LOGIN, { email = '' } = {}) => setAuthDialog({ open: true, mode, email }),
        [],
    );
    const closeAuthDialog = () => setAuthDialog((d) => ({ ...d, open: false }));
    const setAuthMode = (mode) => setAuthDialog((d) => ({ ...d, mode }));
    const [user, setUserFn] = useState(undefined);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);

    useMetaTags();
    const { t } = useTranslation();

    // This will check for existing consent and initialize Amplitude accordingly.
    useEffect(() => {
        initAmplitudeWithConsentCheck();
    }, []);

    // check if user token is still valid
    const checkUser = () => {
        const storedUser = CurrentUser.get();
        if (!storedUser) {
            setUser(null);
            return;
        }

        const request = ProfileService.getProfile();
        request
            .then(() => setUserFn(storedUser))
            .catch(
                catchCallback(() => {
                    // consider any error as auth error
                    setUser(null);
                }),
            );
        return () => request.cancel();
    };

    const alertOnClose = (setter) => (_e, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setter(null);
    };

    const setUser = (user) => {
        // update local storage before setting user
        CurrentUser.set(user);
        setUserFn(user);
    };

    const isNotLogged = !user && user !== undefined;
    const isLogged = Boolean(user);

    useEffect(checkUser, []);

    useEffect(() => {
        return onSessionExpired(() => {
            CurrentUser.set(null);
            setUserFn(null);
            setInfo(t('error.sessionExpired'));
        });
    }, [t]);

    const notifications = buildNotifications({ setError, setInfo, t });

    return (
        <MuiProviders>
            <DictionaryProvider>
                <BrowserRouter>
                    <LoyaltyProvider user={user}>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: '100vh',
                            }}
                        >
                            <Header
                                onLogin={() => openAuthDialog(AUTH_MODE.LOGIN)}
                                onLogout={() => setUser(null)}
                                onSignup={() => openAuthDialog(AUTH_MODE.SIGNUP)}
                                user={user}
                            />
                            <div style={{ flexGrow: '1' }}>
                                <LoyaltyAgbGate isLogged={isLogged} />
                                <Routes>
                                    <Route
                                        path="/booking/*"
                                        element={
                                            <BookingPage user={user} setUser={setUser} notifications={notifications} />
                                        }
                                    />
                                    <Route
                                        path="/profile"
                                        element={
                                            // Render nothing until checkUser() resolves the initial
                                            // undefined state; otherwise the guard would redirect an
                                            // authenticated deep link away before auth is known.
                                            user === undefined ? null : isLogged ? (
                                                <ProfilePage user={user} notifications={notifications} />
                                            ) : (
                                                <Navigate to="/booking" replace />
                                            )
                                        }
                                    />
                                    <Route
                                        path="/loyalty"
                                        element={
                                            user === undefined ? null : isLogged ? (
                                                <LoyaltyPage />
                                            ) : (
                                                <Navigate to="/booking" replace />
                                            )
                                        }
                                    />
                                    <Route
                                        path="/reset/:token"
                                        element={
                                            // Wait for auth to resolve so a reset link isn't redirected
                                            // to /booking during the initial undefined state.
                                            user === undefined ? null : isNotLogged ? (
                                                <ResetPage
                                                    notifications={notifications}
                                                    setUser={setUser}
                                                    openAuthDialog={openAuthDialog}
                                                />
                                            ) : (
                                                <Navigate to="/booking" replace />
                                            )
                                        }
                                    />
                                    <Route path="/info/:code/:number" element={<InfoPage />} />
                                    <Route path="/info/:code" element={<InfoPage />} />
                                    <Route path="/email/unsubscribe/:token" element={<UnsubscribePage />} />
                                    <Route path="/email/confirm/:token" element={<ConfirmEmailPage />} />
                                    <Route path="/emails/:emailId" element={<EmailView />} />
                                    <Route
                                        path="/signup"
                                        element={<SignupRedirect user={user} openAuthDialog={openAuthDialog} />}
                                    />
                                    <Route
                                        path="*"
                                        element={
                                            <Navigate
                                                to={{ pathname: '/booking', search: window.location.search }}
                                                replace
                                            />
                                        }
                                    />
                                </Routes>
                            </div>
                            <Footer />
                        </div>

                        <AuthDialog
                            open={authDialog.open}
                            mode={authDialog.mode}
                            prefillEmail={authDialog.email}
                            onClose={closeAuthDialog}
                            onModeChange={setAuthMode}
                            setUser={setUser}
                            notifications={notifications}
                        />

                        <Snackbar open={Boolean(info)} autoHideDuration={20000} onClose={alertOnClose(setInfo)}>
                            <Alert onClose={alertOnClose(setInfo)} severity="info">
                                {info}
                            </Alert>
                        </Snackbar>

                        <Snackbar open={Boolean(error)} autoHideDuration={10000} onClose={alertOnClose(setError)}>
                            <Alert onClose={alertOnClose(setError)} severity="error">
                                {error}
                            </Alert>
                        </Snackbar>
                    </LoyaltyProvider>
                </BrowserRouter>
                <CookieBanner />
            </DictionaryProvider>
        </MuiProviders>
    );
};

// App no longer owns `needsAgb` — it is derived loyalty state, read straight from the provider.
const LoyaltyAgbGate = ({ isLogged }) => {
    const { needsAgb, markAgbAccepted } = useLoyalty();
    return isLogged ? <AgbConsentModal open={needsAgb} onAccepted={markAgbAccepted} /> : null;
};

const buildNotifications = ({ setError, setInfo, t }) => ({
    showError: (e) => {
        const errorMessage = getErrorMessageFromResponseError(e, t);
        setError(
            <>
                {errorMessage.message}
                {errorMessage.details && errorMessage.details.map((detail, i) => <div key={i}>{detail}</div>)}
            </>,
        );
    },
    clearError: () => {
        setError(null);
    },
    showInfo: (message) => {
        setInfo(message);
    },
    clearInfo: () => {
        setInfo(null);
    },
});

export default App;
